<template>
  <div class="relative w-full overflow-hidden" style="min-height: 500px">
    <div
      class="absolute inset-y-0 left-0 flex flex-col items-center justify-center w-full z-20"
      :style="{
        transform: isPickerOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 1s cubic-bezier(0.22, 1, 0.36, 1)'
      }"
    >
      <div
        class="flex items-center cursor-pointer pointer-events-auto"
        @click="togglePicker"
      >
        <span class="text-lg">{{
          $t('customization.switchToThePredefinedColors')
        }}</span>
        <span class="ml-2 text-xl font-bold">→</span>
      </div>
      <div class="flex sm:flex-row flex-col mt-4">
        <div>
          <div class="mt-4 flex gap-10">
            <div id="colorPicker" ref="colorPicker" @click.stop></div>
          </div>
        </div>
        <div class="ml-0 sm:ml-6">
          <div class="flex flex-col space-y-2 mt-3">
            <select v-model="inputMode" class="p-2 rounded border mb-2">
              <option value="rgb">RGB</option>
              <option value="hex">HEX</option>
            </select>
            <div
              v-if="inputMode === 'rgb'"
              class="flex flex-row sm:flex-col space-x-2"
            >
              <label
                >R<input
                  v-model.number.lazy="rgbInputR"
                  placeholder="R"
                  class="w-12 p-2 mt-1 mx-2 color-value-input-border"
              /></label>
              <label
                >G<input
                  v-model.number.lazy="rgbInputG"
                  placeholder="G"
                  class="w-12 p-2 mt-1 mx-2 color-value-input-border"
              /></label>
              <label
                >B<input
                  v-model.number.lazy="rgbInputB"
                  placeholder="B"
                  class="w-12 p-2 mt-1 mx-2 color-value-input-border"
              /></label>
            </div>
            <div v-if="inputMode === 'hex'" class="flex space-x-2">
              <input
                v-model.trim.lazy="hexInput"
                placeholder="#ff0000"
                class="w-24 p-2 color-value-input-border"
              />
            </div>
          </div>
          <div class="flex">
            <div
              class="mt-4 w-8 h-8 border rounded-full"
              :style="{ backgroundColor: hexInput }"
            ></div>
            <button
              class="mt-4 ml-4 py-2 px-4 bg-sh-primary text-white rounded md:hidden"
              @click="$emit('close')"
            >
              {{ $t('customization.applyColor') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="flex flex-wrap relative z-10 p-2"
      :style="{
        transform: isPickerOpen ? 'translateX(110%)' : 'translateX(0)',
        transition: 'transform 1s cubic-bezier(0.22, 1, 0.36, 1)'
      }"
    >
      <div
        v-if="withColorWheel"
        class="rounded-full flex border-2 w-12 h-12 justify-center items-center cursor-pointer mr-2 mb-2 relative"
        :class="{
          'border-sh-primary':
            selectedColor >= 0x1000000 && selectedColor <= 0x1ffffff
        }"
        :style="{
          background:
            'conic-gradient(blue, aqua, lime, yellow, red, magenta, blue)'
        }"
        @click="togglePicker"
      >
        <base-icon
          v-if="selectedColor >= 0x1000000 && selectedColor <= 0x1ffffff"
          size="xxs"
          name="check-icon"
          class="absolute text-center pin-r pin-b -mb-1 -mr-1 w-4 h-4 bg-sh-primary-lighten-5 rounded-full text-white"
        />
      </div>
      <swatch
        v-for="color in colors"
        :id="color.id"
        :key="color.id"
        :size="12"
        class="mr-2 mb-2"
        :label="color.label"
        :hex="color.hex"
        :selected="color.id === selectedColor"
        @click.native.stop="selectPresetColor(color)"
      />
    </div>
  </div>
</template>

<script>
import Swatch from './Swatch.vue';

import iro from '@jaames/iro';
import { useColorStore } from '@/store-pinia/colors';

export default {
  name: 'ColorPicker',
  components: { Swatch },
  props: {
    colors: {
      type: Array,
      required: true,
      default: () => []
    },
    selectedColor: {
      type: Number,
      required: false,
      default: 0
    },
    withColorWheel: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isPickerOpen: false,
      colorPicker: null,
      inputMode: 'rgb',
      emitting: true
    };
  },

  computed: {
    hexInput: {
      get() {
        if (!this.colorPicker) {
          return null;
        }
        return this.colorPicker.color.hexString;
      },
      set(value) {
        try {
          this.colorPicker.color.hexString = value;
        } catch (err) {
          this.colorPicker.color.hexString = '#fff';
        }
      }
    },
    rgbInputR: {
      get() {
        if (!this.colorPicker) {
          return null;
        }
        return this.colorPicker.color.red;
      },
      set(value) {
        this.colorPicker.color.red = value;
      }
    },
    rgbInputG: {
      get() {
        if (!this.colorPicker) {
          return null;
        }
        return this.colorPicker.color.green;
      },
      set(value) {
        this.colorPicker.color.green = value;
      }
    },
    rgbInputB: {
      get() {
        if (!this.colorPicker) {
          return null;
        }
        return this.colorPicker.color.blue;
      },
      set(value) {
        this.colorPicker.color.blue = value;
      }
    }
  },
  watch: {
    rgbInputR(value) {
      if (isNaN(value)) {
        this.hexInput = '#fff';
      }
    }
  },

  mounted() {
    const colorStore = useColorStore();
    colorStore.loadFontColors();
    this.colorPicker = iro.ColorPicker(this.$refs.colorPicker, {
      width: 200,
      color: '#ffffff',
      layout: [
        {
          component: iro.ui.Box
        },
        {
          component: iro.ui.Slider,
          options: {
            id: 'hue-slider',
            sliderType: 'hue'
          }
        }
      ]
    });

    this.colorPicker.on('color:change', this.selectColorPickerColor);
    this.colorPicker.on('input:start', () => {
      this.emitting = false;
    });
    this.colorPicker.on('input:end', color => {
      this.emitting = true;
      this.selectColorPickerColor(color);
    });

    if (
      !this.isPickerOpen &&
      this.selectedColor >= 0x1000000 &&
      this.selectedColor <= 0x1ffffff
    ) {
      this.togglePicker();
    }
  },

  methods: {
    togglePicker() {
      const colorStore = useColorStore();
      const color = colorStore.color(this.selectedColor);
      if (color.hex) {
        this.hexInput = color.hex;
      }
      this.isPickerOpen = !this.isPickerOpen;
    },
    selectPresetColor(color) {
      this.$emit('selectColor', { id: color.id, source: 'swatch' });
    },
    selectColorPickerColor(color) {
      if (this.emitting && this.isPickerOpen) {
        const id =
          2 ** 24 + (color.red << 16) + (color.green << 8) + color.blue;
        this.$emit('selectColor', { id, source: 'picker' });
      }
    }
  }
};
</script>

<style scoped>
.color-value-input-border {
  @apply border border-solid border-grey-light rounded;
}
</style>
